import { createTabFileClient, TabFileError } from '@tabservice/tabfile-client';
import { QueryClient } from '@tanstack/react-query';
import { authStore } from './auth-store';
import { uploadManager } from './upload-manager';
import { env } from './env';

/** One client for the whole app. Auth comes from the auth store on every call. */
let lastRequestHadToken = false;
export const tabfile = createTabFileClient({
  baseUrl: env.apiBaseUrl,
  getAuth: async () => {
    const token = await authStore.getToken();
    lastRequestHadToken = Boolean(token);
    return token ? { bearer: token } : null;
  },
  onUnauthorized: () => {
    authStore.handleUnauthorized(lastRequestHadToken);
  },
});

/**
 * Nothing fetched for one person may be shown to the next. When the signed-in user changes
 * (sign-out, or a sign-in as somebody else), every cached answer and every remembered
 * upload goes, so the next screen is drawn from the new session alone.
 */
let lastUid: string | null | undefined;
authStore.subscribe(() => {
  const state = authStore.getSnapshot();
  if (state.status === 'loading') return;
  const uid = state.user?.uid ?? null;
  if (lastUid !== undefined && uid !== lastUid) {
    queryClient.clear();
    uploadManager.forgetAll();
  }
  lastUid = uid;
});

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      /**
       * Arriving on a screen asks Tab Service again, but not twice.
       *
       * With no stale time and refetchOnMount: 'always', every screen fetched everything
       * twice: once in the route loader, and again the moment the component mounted, a few
       * hundred milliseconds behind. Crossing in from the portal made four such pairs -
       * /v1/me, /v1/forms, /v1/uploads and /v1/e-files - and the second of each was thrown
       * away, because the first had already filled the cache with the same answer.
       *
       * Thirty seconds is long enough to cover the gap between a loader and its mount, and
       * short enough that walking back to a screen still refreshes it. Anything that must be
       * read fresh every single time sets staleTime: 0 for itself, and those still refetch on
       * mount; the live views drive themselves with refetchInterval, which is untouched.
       */
      staleTime: 30_000,
      refetchOnMount: true,
      gcTime: 5 * 60_000,
      refetchOnWindowFocus: true,
      retry: (failureCount, error) => {
        if (error instanceof TabFileError && error.status < 500) return false;
        // 503 is Tab Service saying "busy, try again" (its database momentarily out of
        // connections). That passes in seconds, so keep asking a while longer before the
        // page gives up, instead of failing on the third quick attempt.
        if (error instanceof TabFileError && error.status === 503) return failureCount < 5;
        return failureCount < 2;
      },
      // 1s, 2s, 4s, 8s, 8s: about 23 seconds in all for a 503, about 3 for anything else.
      retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 8000),
    },
    mutations: {
      retry: false,
    },
  },
});

/** Short readable message for anything thrown by the client. */
export function errorMessage(error: unknown): string {
  if (error instanceof TabFileError) {
    // A 5xx carries nothing a person can act on, and nothing about the server should reach
    // the screen. Every 4xx detail is written for the reader by the API.
    if (error.status >= 500) return 'Something went wrong on our side. Try again in a moment.';
    return error.detail ?? error.title;
  }
  // A failed fetch is the browser's own TypeError ("Failed to fetch", "NetworkError when
  // attempting to fetch resource", "Load failed"). None of that wording belongs on screen.
  if (error instanceof TypeError || (error instanceof Error && /fetch|network/i.test(error.message))) {
    return 'Tab Service could not be reached. Check your connection and try again.';
  }
  if (error instanceof Error) return error.message;
  return 'Something went wrong.';
}
